import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { buildCustomerLedgerPdfHeader } from '../../utils/customerLedgerPdf';
import { exportAsImage } from '../../utils/ImageExporter';
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { Button } from 'reactstrap';
import Select from 'react-select';
import { DatePicker } from 'antd';
import moment from 'moment';
import { getAllPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import { getAccountsReceivableAgewiseReport } from '../../services/ReportServices';
import { displayError } from '../../helpers';
import { HandleSelect, reportMultiSelectComponents, reportFilterSelectStyles } from '../../components/MultiSelectControls';
import ReportExportDropdown from '../../components/ReportExportDropdown';
import {
    DEFAULT_AGE_BUCKETS_TEXT,
    buildColumnsForReportType,
    buildPdfExportColumns,
    inferReportDataType,
    parseAgeBucketsText,
} from '../../utils/accountsReceivableAgewiseUtils';

const filterSelectStyles = reportFilterSelectStyles;

const REPORT_TYPE_OPTIONS = [
    { value: 'summary', label: 'Summary (by Customer)' },
    { value: 'detail', label: 'Detail (by Invoice)' },
];

function AccountsReceivableAgewise() {
    const [cookies] = useCookies(['myToken']);
    const exportRef = useRef(null);
    const [records, setRecords] = useState([]);
    const [bucketTotals, setBucketTotals] = useState({ grand_total: 0 });
    const [activeAgeBuckets, setActiveAgeBuckets] = useState(() => parseAgeBucketsText(DEFAULT_AGE_BUCKETS_TEXT));
    const [ageBucketsText, setAgeBucketsText] = useState(DEFAULT_AGE_BUCKETS_TEXT);
    const [asOnDateLabel, setAsOnDateLabel] = useState('');
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [salesreps, setSalesreps] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [selectedSalesrep, setSelectedSalesrep] = useState([]);
    const [reportType, setReportType] = useState(REPORT_TYPE_OPTIONS[0]);
    const [asOnDate, setAsOnDate] = useState(moment());
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [loadedReportType, setLoadedReportType] = useState(null);
    const hasGeneratedOnce = useRef(false);
    const skipReportTypeFetch = useRef(true);

    const plantsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All plants' }, ...plants],
        [plants],
    );
    const customersWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All customers' }, ...customers],
        [customers],
    );
    const salesrepsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All sales reps' }, ...salesreps],
        [salesreps],
    );

    const resolveMultiIds = (selected, allOptions) => {
        if (!selected || selected.length === 0) {
            return '*';
        }
        if (selected.some((item) => item.value === 'select-all')) {
            return allOptions.map((item) => item.value).join(',') || '*';
        }
        return selected.map((item) => item.value).join(',');
    };

    useEffect(() => {
        getAllPlants(cookies).then((plantList) => {
            setPlants((plantList.plant_list || [])
                .filter((plant) => plant.status)
                .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
        });
        getAllCustomerMain(cookies).then((customerList) => {
            setCustomers((customerList.customer_list || [])
                .filter((customer) => customer.status)
                .map((customer) => ({ value: customer.id, label: customer.name })));
        });
        getAllSalesRepresentatives(cookies).then((salesRepList) => {
            setSalesreps((salesRepList.salesrep_data || [])
                .filter((salesrep) => salesrep.status)
                .map((salesrep) => ({ value: salesrep.id, label: salesrep.entity_name })));
        });
    }, [cookies]);

    const formatAmount = (value) => Number(value || 0).toFixed(2);

    const activeReportType = loadedReportType && loadedReportType === reportType.value
        ? loadedReportType
        : reportType.value;

    const columns = useMemo(() => (
        buildColumnsForReportType(activeReportType, activeAgeBuckets, bucketTotals, formatAmount)
    ), [activeAgeBuckets, activeReportType, bucketTotals]);

    const dataMatchesSelection = loadedReportType === reportType.value;

    const formatReportRow = useCallback((row, buckets) => {
        const formatted = { ...row };
        buckets.forEach((bucket) => {
            if (formatted[bucket.key] != null) {
                formatted[bucket.key] = formatAmount(formatted[bucket.key]);
            }
        });
        if (formatted.total_outstanding != null) {
            formatted.total_outstanding = formatAmount(formatted.total_outstanding);
        }
        if (formatted.outstanding_amount != null) {
            formatted.outstanding_amount = formatAmount(formatted.outstanding_amount);
        }
        if (formatted.invoice_amount != null && formatted.invoice_amount !== '') {
            formatted.invoice_amount = formatAmount(formatted.invoice_amount);
        }
        return formatted;
    }, []);

    const fetchReport = useCallback(() => {
        if (!asOnDate?.isValid()) {
            displayError({ message: 'Select as-on date.' }, 'Load Failed');
            return Promise.resolve();
        }
        let parsedBuckets;
        try {
            parsedBuckets = parseAgeBucketsText(ageBucketsText);
        } catch (error) {
            displayError({ message: error.message }, 'Invalid Age Buckets');
            return Promise.resolve();
        }
        setIsLoading(true);
        return getAccountsReceivableAgewiseReport(cookies, {
            plant_id: resolveMultiIds(selectedPlant, plants),
            customer_ids: resolveMultiIds(selectedCustomer, customers),
            salesrep_ids: resolveMultiIds(selectedSalesrep, salesreps),
            as_on_date: asOnDate.format('DD-MM-YYYY'),
            report_type: reportType.value,
            age_buckets: ageBucketsText.trim() || DEFAULT_AGE_BUCKETS_TEXT,
        })
            .then((response) => {
                const buckets = response.age_buckets?.length
                    ? response.age_buckets
                    : parsedBuckets;
                setActiveAgeBuckets(buckets);
                if (response.age_buckets_text) {
                    setAgeBucketsText(response.age_buckets_text);
                }
                const lines = (response.report_lines || []).map((row) => formatReportRow(row, buckets));
                const responseType = response.report_type
                    || inferReportDataType(lines)
                    || reportType.value;
                setRecords(lines);
                setLoadedReportType(responseType);
                setBucketTotals(response.bucket_totals || { grand_total: 0 });
                setAsOnDateLabel(response.as_on_date || asOnDate.format('DD-MM-YYYY'));
                hasGeneratedOnce.current = true;
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Load Failed');
                setRecords([]);
                setLoadedReportType(null);
            })
            .finally(() => setIsLoading(false));
    }, [
        ageBucketsText,
        asOnDate,
        cookies,
        customers,
        formatReportRow,
        plants,
        reportType.value,
        salesreps,
        selectedCustomer,
        selectedPlant,
        selectedSalesrep,
    ]);

    useEffect(() => {
        if (skipReportTypeFetch.current) {
            skipReportTypeFetch.current = false;
            return;
        }
        if (!hasGeneratedOnce.current) {
            setRecords([]);
            setLoadedReportType(null);
            setBucketTotals({ grand_total: 0 });
            return;
        }
        fetchReport();
    }, [reportType.value]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReportTypeChange = (option) => {
        if (!option || option.value === reportType.value) {
            return;
        }
        setReportType(option);
        setRecords([]);
        setLoadedReportType(null);
        setBucketTotals({ grand_total: 0 });
    };

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return records;
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return records.filter((row) => {
            const haystack = Object.values(row)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');
            return terms.every((term) => haystack.includes(term));
        });
    }, [records, searchText]);

    const exportFileBase = useMemo(() => {
        const typePart = reportType.value === 'detail' ? 'Detail' : 'Summary';
        const datePart = asOnDate?.isValid() ? asOnDate.format('DD-MM-YYYY') : '';
        return `AccountsReceivableAgewise_${typePart}_${datePart}`;
    }, [asOnDate, reportType.value]);

    const pdfSummaryLines = useMemo(() => (
        asOnDateLabel ? [`As on: ${asOnDateLabel}    Age buckets: ${ageBucketsText}`] : []
    ), [ageBucketsText, asOnDateLabel]);

    const ensureExportData = () => {
        if (!filteredRecords.length) {
            displayError({ message: 'Generate the report first, then export.' }, 'Export Failed');
            return false;
        }
        if (!dataMatchesSelection) {
            displayError(
                { message: 'Report type changed. Wait for reload to finish or click Generate.' },
                'Export Failed',
            );
            return false;
        }
        const inferredType = inferReportDataType(filteredRecords);
        if (inferredType && inferredType !== reportType.value) {
            displayError(
                { message: 'Displayed data does not match the selected report type. Click Generate.' },
                'Export Failed',
            );
            return false;
        }
        return true;
    };

    const handleCSVExport = () => {
        if (!ensureExportData()) return;
        exportAsCSV(columns, filteredRecords, exportFileBase);
    };

    const handleExcelExport = () => {
        if (!ensureExportData()) return;
        exportAsExcel(columns, filteredRecords, exportFileBase);
    };

    const handlePDFExport = async () => {
        if (!ensureExportData()) return;
        setIsLoading(true);
        try {
            const header = await buildCustomerLedgerPdfHeader(cookies, selectedPlant, plants, {
                documentTitle: 'Accounts Receivable — Age Wise',
                subtitle: `${reportType.label} as on ${asOnDateLabel}`,
            });
            const exportColumns = buildColumnsForReportType(
                loadedReportType || reportType.value,
                activeAgeBuckets,
                bucketTotals,
                formatAmount,
            );
            const pdfColumns = buildPdfExportColumns(exportColumns, bucketTotals, formatAmount);
            const blob = createPdfBlob('', pdfColumns, filteredRecords, pdfSummaryLines, header);
            openPdfInApp(blob, {
                title: 'Accounts Receivable Age Wise',
                downloadFileName: `${exportFileBase}.pdf`,
            });
        } catch (error) {
            displayError({ message: error?.message || 'PDF export failed.' }, 'Export Failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageExport = async () => {
        if (!ensureExportData()) return;
        const element = exportRef.current;
        element?.classList.add('is-exporting-image');
        try {
            await exportAsImage(element, `${exportFileBase}.png`);
        } catch (error) {
            displayError({ message: error?.message || 'Image export failed.' }, 'Export Failed');
        } finally {
            element?.classList.remove('is-exporting-image');
        }
    };

    const exportDisabled = isLoading || !filteredRecords.length || !dataMatchesSelection;

    const exportButtons = (
        <ReportExportDropdown
            onCSV={handleCSVExport}
            onExcel={handleExcelExport}
            onPDF={handlePDFExport}
            onImage={handleImageExport}
            disabled={exportDisabled}
        />
    );

    const toolbarFilters = (
        <>
            <div className="list-page-toolbar-filters-row">
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter list-page-toolbar-filter-plant">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            options={plantsWithSelectAll}
                            value={selectedPlant}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedPlant,
                                setSelectedPlant,
                                plants,
                                plantsWithSelectAll,
                            )}
                            placeholder="Plant"
                            styles={filterSelectStyles}
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            options={customersWithSelectAll}
                            value={selectedCustomer}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedCustomer,
                                setSelectedCustomer,
                                customers,
                                customersWithSelectAll,
                            )}
                            placeholder="Customer"
                            styles={filterSelectStyles}
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            options={salesrepsWithSelectAll}
                            value={selectedSalesrep}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedSalesrep,
                                setSelectedSalesrep,
                                salesreps,
                                salesrepsWithSelectAll,
                            )}
                            placeholder="Sales Rep"
                            styles={filterSelectStyles}
                        />
                    </div>
                </div>
            </div>
            <div className="list-page-toolbar-filters-row">
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-buckets-group">
                        <input
                            type="text"
                            className="list-page-toolbar-text-input"
                            value={ageBucketsText}
                            onChange={(event) => setAgeBucketsText(event.target.value)}
                            placeholder="Age buckets (0-30,31-60,61-120,121+)"
                            title="Comma-separated ranges, e.g. 0-30,31-60,61-120,121+"
                        />
                        <Button
                            color="secondary"
                            outline
                            className="list-page-toolbar-reset-btn"
                            onClick={() => setAgeBucketsText(DEFAULT_AGE_BUCKETS_TEXT)}
                            disabled={isLoading}
                            title="Reset age buckets to default"
                        >
                            Default
                        </Button>
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            classNamePrefix="list-page-filter-select"
                            options={REPORT_TYPE_OPTIONS}
                            value={reportType}
                            onChange={handleReportTypeChange}
                            placeholder="Report Type"
                            styles={filterSelectStyles}
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter-date">
                        <DatePicker
                            value={asOnDate}
                            format="DD-MM-YYYY"
                            onChange={(value) => value && setAsOnDate(value)}
                            placeholder="As on Date"
                        />
                    </div>
                </div>
            </div>
            <div className="list-page-toolbar-filters-row list-page-toolbar-filters-row-actions">
                <div className="list-page-toolbar-slot">
                    {exportButtons}
                </div>
                <div className="list-page-toolbar-slot">
                    <Button
                        color="primary"
                        className="list-page-toolbar-generate-btn"
                        onClick={fetchReport}
                        disabled={isLoading}
                    >
                        Generate
                    </Button>
                </div>
            </div>
        </>
    );

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Accounts Receivable — Age Wise"
                    subtitle="Outstanding invoice balances grouped by age. Default buckets: 0-30, 31-60, 61-120, 121+ days — change before Generate if needed."
                    modeLabel="REPORT"
                    toolbarVariant="stacked"
                    recordCount={dataMatchesSelection ? filteredRecords.length : 0}
                    recordLabel={reportType.value === 'detail' ? 'invoice lines' : 'customers'}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    searchPlaceholder="Search customer, sales rep, amount..."
                    toolbarFilters={toolbarFilters}
                    reportListLink="/ReportAll"
                >
                    <div ref={exportRef} className="ledger-export-capture">
                        <div className="ledger-export-meta">
                            <strong>Accounts Receivable — Age Wise</strong>
                            {` — ${reportType.label}`}
                            {asOnDateLabel ? ` (As on ${asOnDateLabel})` : ''}
                            {ageBucketsText ? ` | Buckets: ${ageBucketsText}` : ''}
                        </div>
                        <div className="item-list-table-container">
                            <div className="table-responsive">
                                <ReactTableWrapper
                                    key={`${loadedReportType || 'empty'}-${reportType.value}`}
                                    title="Accounts Receivable Age Wise"
                                    columns={columns}
                                    data={dataMatchesSelection ? filteredRecords : []}
                                    hideDelete
                                    hidePrint
                                    onCSVExport={handleCSVExport}
                                    onPDFExport={handlePDFExport}
                                    enableTableSearch={false}
                                    isLoading={isLoading}
                                    compactRows
                                    layoutFullWidth={activeReportType === 'detail'}
                                />
                            </div>
                        </div>
                    </div>
                </ListPageLayout>
            </div>
        </div>
    );
}

export default AccountsReceivableAgewise;
